import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';

// actions
import { getCart as getCartAction } from 'actions/CartActions';
import {
  fetchAvailableSamplesCount as fetchAvailableSamplesCountAction,
  fetchCardSuiteDetails as fetchCardSuiteDetailsAction,
  fetchDefaultTemplates as fetchDefaultTemplatesAction,
  fetchUserShippingAddress as fetchUserShippingAddressAction,
  resetSingleSampleFlow,
  setCustomizationsData,
  setStepKey as setStepKeyAction,
} from 'cards/reducers/singleSampleFlow/actions';
import { hideModal, openModal } from 'cards/actions/modalActions';
import { getUserContext as getUserContextAction } from 'actions/UserActions';
import { setMultiSampleEnabled } from 'cards/actions/singlesample';

// selectors
import { getIsAdminView } from 'cards/selectors/cardCatalogSelector';
import {
  getCardSuiteDetailsSet,
  getCardType,
  getCustomizationsSet,
  getShippingDataFetched,
  getSingleSampleAvailable,
  getStepKey,
  getTemplatesSet,
} from 'cards/selectors/singleSampleFlow';
import { getUserContext, getUserIsGuest } from 'selectors/user/userSelectors';

// helpers
import {
  lookupDefaultVariation,
  lookupSelectedVariation,
} from 'cards/components/common/SingleSample/util/helpers';
import { Customizations, CustomizationsSet } from 'cards/reducers/singleSampleFlow/reducer';
import { AppDispatch, RootState } from 'reducers/index';
import generateUUID from '../../../../../lib/uuid';
import { COMPONENTS, STEPS } from './constants';
import { trackEvent } from '../../../../../util/trackingHelper';

import styles from './styles.module.less';

export interface ParentProps {
  cardNameList: string[];
  cardSuiteUUIDList: string[];
  cardUUIDList: string[];
  entryPoint: string;
  flowName: string;
  isMagnet: boolean;
  originalModalOptions: any;
  selectedColorSet: any;
}

interface StateProps {
  cardSuiteDetailsSet: any;
  cardType: string | null;
  cart: any;
  customizationsSet: any;
  isAdminView: boolean;
  isGuest: boolean;
  shippingDataFetched: boolean;
  singleSampleAvailable: boolean | null;
  stepKey: string;
  templatesSet: any;
  userContext: any;
}

interface DispatchProps {
  closeModal: (shouldNotDisableMulti?: boolean) => void;
  fetchAvailableSamplesCount: (cardType: string) => void;
  fetchCardSuiteDetails: any;
  fetchCart: any;
  fetchDefaultTemplates: any;
  fetchUserContext: () => void;
  fetchUserShippingAddress: (userContext: any) => void;
  saveCustomizationsData: (data: CustomizationsSet) => void;
  setStepKey: (stepKey: string) => void;
  showNewSamplesFlowModal: (props: any, overrideOptions?: any) => void;
  showPaperTypeModal: (props: any, modalOptions: any) => void;
}

type NewSingleSampleFlowProps = ParentProps & StateProps & DispatchProps;

const NewSingleSampleFlow = (props: NewSingleSampleFlowProps): JSX.Element | null => {
  const {
    cardNameList,
    cardSuiteDetailsSet,
    cardSuiteUUIDList,
    cardUUIDList,
    cardType,
    cart,
    closeModal,
    customizationsSet,
    entryPoint,
    flowName,
    fetchAvailableSamplesCount,
    fetchCardSuiteDetails,
    fetchDefaultTemplates,
    fetchUserContext,
    fetchUserShippingAddress,
    isAdminView,
    isGuest,
    isMagnet,
    originalModalOptions,
    saveCustomizationsData,
    selectedColorSet,
    setStepKey,
    shippingDataFetched,
    showNewSamplesFlowModal,
    showPaperTypeModal,
    singleSampleAvailable,
    stepKey,
    templatesSet,
    userContext,
  } = props;

  const [flowUUID] = useState(generateUUID());
  const [trackingParams, setTrackingParams] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (cart.size > 0 || (singleSampleAvailable !== null && !singleSampleAvailable)) {
      setStepKey(STEPS.ERROR);
    }

    if (singleSampleAvailable === null && cardType !== null) {
      fetchAvailableSamplesCount(cardType);
    }

    if (!shippingDataFetched && !isGuest) {
      fetchUserShippingAddress(userContext);
    }

    if (Object.keys(cardSuiteDetailsSet).length === 0) {
      fetchCardSuiteDetails(cardSuiteUUIDList, isAdminView);
    }

    if (
      Object.keys(customizationsSet).length === 0 &&
      selectedColorSet &&
      Object.keys(cardSuiteDetailsSet).length > 0
    ) {
      const initialCustomizationsSet: { [key: string]: Customizations } = {};

      Object.entries(cardSuiteDetailsSet).forEach(([key, suiteDetails]: any) => {
        const selectedColor = selectedColorSet[key];
        const defaultVariation = lookupDefaultVariation(suiteDetails, isMagnet);

        // lookup selected color variation unless letterpress or foil (then use default)
        const colorVariation = lookupSelectedVariation(
          {
            ...defaultVariation.option_values,
            color:
              suiteDetails.letterpress || suiteDetails.foil
                ? defaultVariation.option_values.color
                : selectedColor,
          },
          suiteDetails,
          isMagnet
        );

        initialCustomizationsSet[key] = {
          color: colorVariation.option_values.color,
          uploadcareFile: null,
          variation: colorVariation,
        };
      });

      saveCustomizationsData(initialCustomizationsSet);
    }

    if (Object.keys(templatesSet).length === 0 && Object.keys(cardSuiteDetailsSet).length > 0) {
      fetchDefaultTemplates(cardSuiteUUIDList, cardSuiteDetailsSet, {});
    }
  }, [
    cardSuiteUUIDList,
    fetchCardSuiteDetails,
    isAdminView,
    saveCustomizationsData,
    selectedColorSet,
    templatesSet,
    cardSuiteDetailsSet,
    fetchDefaultTemplates,
    customizationsSet,
    cart,
    isGuest,
  ]);

  // Send "Flow Started" event when loading is complete
  useEffect(() => {
    if (Object.keys(cardSuiteDetailsSet).length === 0) {
      return;
    }

    const params = {
      entry_point: entryPoint,
      flow_run_id: flowUUID,
      flow_name: flowName,
      business_unit: 'INVITATIONS',
      product_id: cardSuiteUUIDList,
      sku_id: cardUUIDList,
      category: cardType,
      name: cardNameList,
      brand: Object.values(cardSuiteDetailsSet).map((details: any) => details.name),
    };

    setTrackingParams(params);
    trackEvent('Flow Started', params);
  }, [
    entryPoint,
    flowName,
    cardSuiteUUIDList,
    cardUUIDList,
    cardType,
    cardNameList,
    cardSuiteDetailsSet,
    flowUUID,
  ]);

  // Send "Flow Step Viewed" event every time the user changes steps.
  useEffect(() => {
    if (stepKey && Object.keys(trackingParams).length > 0) {
      trackEvent('Flow Step Viewed', {
        ...trackingParams,
        step_name: stepKey,
      });
    }
  }, [stepKey, trackingParams]);

  const closeCallback = () =>
    showNewSamplesFlowModal({
      entryPoint: 'PLP_MULTI',
      cardSuiteUUIDList,
      selectedColorSet,
      originalModalOptions,
    });

  const loginCloseCallback = () => {
    if (cardType === null) {
      return;
    }

    fetchUserContext();
    fetchAvailableSamplesCount(cardType);
    closeCallback();
  };

  const handleLogin = useCallback(() => {
    closeModal(true);

    setTimeout(() => {
      window.zolaNav.openLoginModal({
        loginCallback: loginCloseCallback,
        closeCallback: loginCloseCallback,
      });
    });
  }, [closeCallback, closeModal]);

  const handleShowPaperTypeModal = useCallback(() => {
    return showPaperTypeModal(
      {},
      {
        closeCallback,
        style: { marginTop: 0 },
        preventClose: true,
      }
    );
  }, [showPaperTypeModal]);

  if (
    !cardSuiteDetailsSet ||
    Object.keys(cardSuiteDetailsSet).length === 0 ||
    singleSampleAvailable === null
  ) {
    return null;
  }

  const StepComponent = COMPONENTS[stepKey];

  return (
    <div className={styles.sampleFlowWrapper}>
      <StepComponent
        cardSuiteUUIDList={cardSuiteUUIDList}
        closeModal={closeModal}
        isAdminView={isAdminView}
        isGuest={isGuest}
        isMagnet={isMagnet}
        login={handleLogin}
        showPaperTypeModal={handleShowPaperTypeModal}
        singleSampleAvailable={singleSampleAvailable}
        trackingParams={trackingParams}
      />
    </div>
  );
};

const mapStateToProps = (state: RootState): StateProps => ({
  cart: state.cart,
  cardSuiteDetailsSet: getCardSuiteDetailsSet(state),
  cardType: getCardType(state),
  customizationsSet: getCustomizationsSet(state),
  isAdminView: getIsAdminView(state),
  isGuest: getUserIsGuest(state),
  shippingDataFetched: getShippingDataFetched(state),
  singleSampleAvailable: getSingleSampleAvailable(state),
  stepKey: getStepKey(state),
  templatesSet: getTemplatesSet(state),
  userContext: getUserContext(state),
});

const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  closeModal: (shouldNotDisableMulti?: boolean) => {
    if (shouldNotDisableMulti) {
      return dispatch(hideModal());
    }

    return dispatch(setMultiSampleEnabled(null, false))
      .then(() => dispatch(resetSingleSampleFlow()))
      .then(() => dispatch(hideModal()));
  },
  fetchAvailableSamplesCount: (cardType: string) =>
    dispatch(fetchAvailableSamplesCountAction(cardType)),
  fetchCardSuiteDetails: (cardSuiteUUIDList: string[], isAdminView: boolean) =>
    dispatch(fetchCardSuiteDetailsAction(cardSuiteUUIDList, isAdminView)),
  fetchCart: getCartAction,
  fetchDefaultTemplates: (cardSuiteUUIDList: string[], cardSuiteDetailsSet: any) =>
    dispatch(fetchDefaultTemplatesAction(cardSuiteUUIDList, cardSuiteDetailsSet, {})),
  fetchUserContext: () => dispatch(getUserContextAction()),
  fetchUserShippingAddress: (userContext: any) =>
    dispatch(fetchUserShippingAddressAction(userContext)),
  saveCustomizationsData: (data: CustomizationsSet) => dispatch(setCustomizationsData(data)),
  setStepKey: (stepKey: string) => dispatch(setStepKeyAction(stepKey)),
  showPaperTypeModal: (props, modalOptions) =>
    dispatch(openModal('PAPER_TYPE', props, modalOptions)),
  showNewSamplesFlowModal: (props, overrideOptions = {}) =>
    dispatch(
      openModal('NEW_SINGLE_SAMPLE_MODAL', props, {
        warnOnClose: true,
        bottomModalOnMobile: true,
        className: styles.modal,
        ...overrideOptions,
      })
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(NewSingleSampleFlow);
